'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import Clarity from '@microsoft/clarity';
import { useAnalyticsConsent } from '@/hooks/useAnalyticsConsent';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { useDashboardTheme } from '@/contexts/DashboardThemeContext';
import { isExcludedClarityRuntime, clearFirstPartyClarityCookies } from '@/lib/clarityRuntime';
import { setAnalyticsTag } from '@/lib/analyticsEvents';

// Age-policy note: Ertzyx's stated eligibility floor is 16 (see /terms-of-service
// and /privacy-policy), but Microsoft Clarity's own terms prohibit use on sites/apps
// targeting users under 18 ("Clarity shouldn't be used on any websites/apps targeting
// users under the age of 18" -- learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api).
// Ertzyx collects no date-of-birth and has no age-gating, so this conflict cannot be
// resolved in code for the 16-17 age band. Flagged for legal/product review -- do not
// silently assume this is resolved.

const CLARITY_PROJECT_ID = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;

// Module-level: survives re-renders and remounts within the same browser tab so
// Clarity.init() is called at most once per page session, never re-triggered by a
// later consent grant.
let clarityLoaded = false;

type ClarityConsentState = 'granted' | 'denied';

interface ClarityAnalyticsProps {
  enabled: boolean;
}

export default function ClarityAnalytics({ enabled }: ClarityAnalyticsProps) {
  const consented = useAnalyticsConsent();
  const lastSentConsent = useRef<ClarityConsentState | null>(null);
  const pathname = usePathname();
  const { profile } = useAuth();
  const { locale } = useLanguage();
  const { theme } = useDashboardTheme();

  useEffect(() => {
    if (!enabled || !CLARITY_PROJECT_ID || isExcludedClarityRuntime()) return;

    if (consented) {
      if (!clarityLoaded) {
        Clarity.init(CLARITY_PROJECT_ID);
        clarityLoaded = true;
      }
      if (lastSentConsent.current !== 'granted') {
        // Ad storage stays permanently denied -- Clarity is used for product
        // analytics here, never for advertising.
        Clarity.consentV2({ ad_Storage: 'denied', analytics_Storage: 'granted' });
        lastSentConsent.current = 'granted';
      }
    } else if (clarityLoaded && lastSentConsent.current !== 'denied') {
      Clarity.consentV2({ ad_Storage: 'denied', analytics_Storage: 'denied' });
      clearFirstPartyClarityCookies();
      lastSentConsent.current = 'denied';
    }
  }, [enabled, consented]);

  useEffect(() => {
    if (!enabled || !consented) return;
    const routeGroup = pathname?.split('/').filter(Boolean)[0] || 'root';
    setAnalyticsTag('route_group', routeGroup);
    setAnalyticsTag('theme', theme);
    setAnalyticsTag('locale', locale);
    if (profile?.role) setAnalyticsTag('account_type', profile.role);
    if (profile?.subscription_tier) setAnalyticsTag('plan_tier', profile.subscription_tier);
  }, [enabled, consented, pathname, theme, locale, profile?.role, profile?.subscription_tier]);

  return null;
}
